import { useState, useMemo } from 'react';
import { Plus, Eye, Edit2, Trash2, Search, Download, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/PageHeader';
import { computar, fmtBRL, fmtUSD, fmtDateBR, diffDays, todayISO } from '../lib/cambio-calc';

const STATUS_CHIPS = [
  { key: 'todos',      label: 'Todos' },
  { key: 'aberto',     label: 'Em aberto' },
  { key: 'liquidado',  label: 'Liquidados' },
  { key: 'vence-7d',   label: 'Vence ≤ 7 dias' },
];

const TIPO_CHIPS = [
  { key: 'todos',         label: 'Todos' },
  { key: 'ndf',           label: 'NDF' },
  { key: 'cambio_pronto', label: 'Câmbio pronto' },
  { key: 'trava',         label: 'Trava' },
];

const TIPO_LABELS = {
  ndf:           'NDF',
  cambio_pronto: 'Câmbio pronto',
  trava:         'Trava',
  d0:            'D0',
  d1:            'D1',
  outro:         'Outro',
};

function TipoBadge({ tipo }) {
  const lbl = TIPO_LABELS[tipo] || tipo;
  const cor = tipo === 'ndf' ? '#3be476'
    : tipo === 'trava' ? '#60a5fa'
    : tipo === 'cambio_pronto' ? '#a78bfa'
    : '#9ca3af';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
      textTransform: 'uppercase',
      background: `${cor}1A`, color: cor, whiteSpace: 'nowrap',
    }}>{lbl}</span>
  );
}

function StatusPill({ status, diasRest }) {
  let cls, lbl;
  if (status === 'aberto' && diasRest >= 0 && diasRest <= 7) {
    cls = 'vence';
    lbl = `Vence em ${diasRest}d`;
  } else if (status === 'aberto') {
    cls = 'aberto';
    lbl = 'Em aberto';
  } else if (status === 'liquidado') {
    cls = 'liquidado';
    lbl = 'Liquidado';
  } else {
    cls = 'cancelado';
    lbl = 'Cancelado';
  }
  const bg = {
    aberto:    'rgba(59,228,118,0.10)',
    liquidado: 'rgba(96,165,250,0.10)',
    vence:     'rgba(245,158,11,0.12)',
    cancelado: 'rgba(122,129,140,0.18)',
  }[cls];
  const fg = {
    aberto:    'var(--lv-green, #3be476)',
    liquidado: '#60a5fa',
    vence:     '#f59e0b',
    cancelado: 'var(--muted)',
  }[cls];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 99,
      fontSize: 11, fontWeight: 600, letterSpacing: '.03em',
      background: bg, color: fg, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: fg }} />
      {lbl}
    </span>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{
        fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
        letterSpacing: '.1em', fontWeight: 600,
      }}>{label}</div>
      <div className="font-display" style={{
        fontSize: 26, fontWeight: 600, margin: '6px 0 2px',
        letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
        color: accent ? 'var(--primary)' : 'var(--ink)',
      }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{sub}</div>
    </div>
  );
}

export function CambioHome({ fechamentos, loading, onNew, onView, onEdit, onDelete, onDuplicate, onLiquidar }) {
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [empresaFilter, setEmpresaFilter] = useState('todas');
  const [mesFilter, setMesFilter] = useState('todos'); // 'YYYY-MM' ou 'todos'

  // Computa derivados pra todos (uma vez)
  const dados = useMemo(() => {
    const hoje = todayISO();
    return (fechamentos || []).map(f => {
      const c = computar(f);
      c.dias_rest = diffDays(hoje, f.dt_vencimento);
      return c;
    });
  }, [fechamentos]);

  // Lista de empresas únicas (pra dropdown) — ordenada
  const empresasDisponiveis = useMemo(() => {
    const set = new Set();
    dados.forEach(d => { if (d.empresa_nome) set.add(d.empresa_nome); });
    return Array.from(set).sort();
  }, [dados]);

  // Lista de meses únicos (YYYY-MM) — mais recentes primeiro
  const mesesDisponiveis = useMemo(() => {
    const set = new Set();
    dados.forEach(d => {
      if (d.dt_inicio && d.dt_inicio.length >= 7) {
        set.add(d.dt_inicio.slice(0, 7));
      }
    });
    return Array.from(set).sort().reverse();
  }, [dados]);

  // Filtrados pra tabela
  const filtrados = useMemo(() => {
    let arr = dados;
    if (statusFilter === 'aberto') arr = arr.filter(d => d.status === 'aberto');
    else if (statusFilter === 'liquidado') arr = arr.filter(d => d.status === 'liquidado');
    else if (statusFilter === 'vence-7d') arr = arr.filter(d => d.status === 'aberto' && d.dias_rest >= 0 && d.dias_rest <= 7);
    if (tipoFilter !== 'todos') arr = arr.filter(d => d.tipo_operacao === tipoFilter);
    if (empresaFilter !== 'todas') arr = arr.filter(d => d.empresa_nome === empresaFilter);
    if (mesFilter !== 'todos') arr = arr.filter(d => (d.dt_inicio || '').slice(0, 7) === mesFilter);
    if (busca.trim()) {
      const t = busca.toLowerCase();
      arr = arr.filter(d =>
        (d.codigo_unico || '').toLowerCase().includes(t) ||
        (d.empresa_nome || '').toLowerCase().includes(t) ||
        (d.operador || '').toLowerCase().includes(t) ||
        (d.cliente_final || '').toLowerCase().includes(t)
      );
    }
    return arr;
  }, [dados, statusFilter, tipoFilter, empresaFilter, mesFilter, busca]);

  // KPIs (sempre sobre TODOS os abertos, nao filtrados)
  const kpis = useMemo(() => {
    const abertos = dados.filter(d => d.status === 'aberto');
    const usdAberto = abertos.reduce((s, d) => s + (Number(d.princ_usd) || 0), 0);
    const totalCred = abertos.reduce((s, d) => s + (Number(d.total_credito) || 0), 0);
    const ponderTotal = usdAberto;
    const taxaMedia = ponderTotal > 0
      ? abertos.reduce((s, d) => s + (d.taxa_final * (Number(d.princ_usd) || 0)), 0) / ponderTotal
      : 0;
    const prox = abertos
      .filter(d => d.dias_rest >= 0)
      .sort((a, b) => a.dias_rest - b.dias_rest)[0];
    return { qtd: abertos.length, usdAberto, totalCred, taxaMedia, prox };
  }, [dados]);

  return (
    <div className="fade-in" style={{ maxWidth: 1500, margin: '0 auto' }}>
      <PageHeader
        title="Fechamento de câmbio"
        subtitle={'Controle de operações de câmbio (NDF, câmbio pronto, trava) · substitui o "Resumo Fechamento Dólar.xlsx"'}
        actions={
          <>
            <Button variant="secondary" onClick={() => exportarCSV(filtrados)} icon={Download}>CSV</Button>
            <Button onClick={onNew} icon={Plus}>Novo fechamento</Button>
          </>
        }
      />

      {/* KPIs */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14,
        marginBottom: 24,
      }}>
        <KPI accent
          label="Em aberto · USD"
          value={`US$ ${fmtUSD(kpis.usdAberto, 0)}`}
          sub={`${kpis.qtd} operação${kpis.qtd === 1 ? '' : 'es'}`} />
        <KPI
          label="A receber · R$"
          value={`R$ ${fmtBRL(kpis.totalCred)}`}
          sub="Total Crédito previsto" />
        <KPI
          label="Taxa média líquida"
          value={fmtBRL(kpis.taxaMedia, 4)}
          sub="Média ponderada por USD" />
        <KPI
          label="Próximo vencimento"
          value={kpis.prox ? fmtDateBR(kpis.prox.dt_vencimento) : '—'}
          sub={kpis.prox
            ? `em ${kpis.prox.dias_rest} dia${kpis.prox.dias_rest === 1 ? '' : 's'} · ${kpis.prox.codigo_unico}`
            : 'sem aberturas'} />
      </div>

      {/* Filtros */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 18,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{ position: 'relative', width: 280 }}>
          <Search size={14} style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--muted)',
          }} />
          <input
            className="input"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Buscar código, empresa, operador..."
            style={{ paddingLeft: 36 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {STATUS_CHIPS.map(c => {
            const active = statusFilter === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setStatusFilter(c.key)}
                style={{
                  height: 30, padding: '0 12px', borderRadius: 99,
                  border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                  background: active ? 'var(--primary-soft, rgba(59,228,118,0.12))' : 'transparent',
                  color: active ? 'var(--primary)' : 'var(--ink-2)',
                  fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >{c.label}</button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {TIPO_CHIPS.map(c => {
            const active = tipoFilter === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setTipoFilter(c.key)}
                style={{
                  height: 30, padding: '0 12px', borderRadius: 99,
                  border: `1px solid ${active ? 'var(--ink-2)' : 'var(--border)'}`,
                  background: active ? 'var(--surface-2)' : 'transparent',
                  color: active ? 'var(--ink)' : 'var(--muted)',
                  fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >{c.label}</button>
            );
          })}
        </div>
      </div>

      {/* Segunda linha — Filtros de Empresa e Mês (dropdowns) */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 18,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
            letterSpacing: '.08em', fontWeight: 600,
          }}>Empresa</span>
          <select
            className="select"
            value={empresaFilter}
            onChange={e => setEmpresaFilter(e.target.value)}
            style={{ height: 34, minWidth: 240, fontSize: 13 }}
          >
            <option value="todas">Todas ({empresasDisponiveis.length})</option>
            {empresasDisponiveis.map(emp => (
              <option key={emp} value={emp}>{emp}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
            letterSpacing: '.08em', fontWeight: 600,
          }}>Mês</span>
          <select
            className="select"
            value={mesFilter}
            onChange={e => setMesFilter(e.target.value)}
            style={{ height: 34, minWidth: 180, fontSize: 13 }}
          >
            <option value="todos">Todos</option>
            {mesesDisponiveis.map(m => {
              const [y, mo] = m.split('-');
              const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
              const lbl = `${meses[parseInt(mo,10)-1]}/${y}`;
              return <option key={m} value={m}>{lbl}</option>;
            })}
          </select>
        </div>
        {(empresaFilter !== 'todas' || mesFilter !== 'todos') && (
          <button
            onClick={() => { setEmpresaFilter('todas'); setMesFilter('todos'); }}
            style={{
              height: 30, padding: '0 12px', borderRadius: 99,
              border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--muted-2)', fontFamily: 'inherit', fontSize: 11.5,
              fontWeight: 600, cursor: 'pointer',
            }}
          >× Limpar empresa/mês</button>
        )}
        <div style={{
          marginLeft: 'auto', fontSize: 12, color: 'var(--muted)',
          fontWeight: 500,
        }}>
          {filtrados.length} de {dados.length} fechamentos
        </div>
      </div>

      {/* Tabela */}
      <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
        <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <Th>Código · Tipo</Th>
              <Th>Empresa · Cliente</Th>
              <Th>Período</Th>
              <Th numeric>USD</Th>
              <Th numeric>Spot</Th>
              <Th numeric>Taxa contratada</Th>
              <Th numeric>Total R$</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Carregando…</td></tr>
            )}
            {!loading && filtrados.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 60, textAlign: 'center', color: 'var(--muted)' }}>
                Nenhum fechamento encontrado.
              </td></tr>
            )}
            {!loading && filtrados.map(d => (
              <tr key={d.id} style={{ cursor: 'pointer', borderTop: '1px solid var(--border)' }}
                onClick={() => onView?.(d)}
                onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                <Td>
                  <div className="font-mono" style={{ fontSize: 11.5 }}>
                    <b style={{ color: 'var(--ink)' }}>{d.codigo_unico}</b>
                  </div>
                  <div style={{ marginTop: 3 }}>
                    <TipoBadge tipo={d.tipo_operacao || 'ndf'} />
                  </div>
                </Td>
                <Td>
                  <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: 12.5 }}>
                    {(d.empresa_nome || '').length > 24
                      ? (d.empresa_nome || '').slice(0, 24) + '…'
                      : (d.empresa_nome || '—')}
                  </div>
                  {d.cliente_final && (
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      → {d.cliente_final.length > 26 ? d.cliente_final.slice(0, 26) + '…' : d.cliente_final}
                    </div>
                  )}
                </Td>
                <Td>
                  <div style={{ fontSize: 12.5 }}>
                    {fmtDateBR(d.dt_inicio)} → {fmtDateBR(d.dt_vencimento)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {d.dc} dias{d.tipo_operacao === 'ndf' ? ` · ${d.du} úteis` : ''}
                  </div>
                </Td>
                <Td numeric>US$ {fmtUSD(d.princ_usd, 0)}</Td>
                <Td numeric>{fmtBRL(d.spot, 4)}</Td>
                <Td numeric><b>{fmtBRL(d.spot_venda || d.taxa_final, 4)}</b></Td>
                <Td numeric className="font-money">R$ {fmtBRL(
                  d.tipo_operacao === 'ndf'
                    ? d.total_credito
                    : (Number(d.princ_usd) * Number(d.spot_venda || d.spot))
                )}</Td>
                <Td><StatusPill status={d.status} diasRest={d.dias_rest} /></Td>
                <Td>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <IconBtn title="Ver"    onClick={e => { e.stopPropagation(); onView?.(d); }}><Eye size={14} /></IconBtn>
                    <IconBtn title="Editar" onClick={e => { e.stopPropagation(); onEdit?.(d); }}><Edit2 size={14} /></IconBtn>
                    <IconBtn title="Duplicar" onClick={e => { e.stopPropagation(); onDuplicate?.(d); }}><Copy size={14} /></IconBtn>
                    {d.status === 'aberto' && (
                      <IconBtn title="Marcar como liquidado" onClick={e => { e.stopPropagation(); onLiquidar?.(d); }}><CheckCircle2 size={14} /></IconBtn>
                    )}
                    <IconBtn title="Excluir" danger onClick={e => { e.stopPropagation(); onDelete?.(d); }}><Trash2 size={14} /></IconBtn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Helpers de celula ─────────────────────────────────────────────
function Th({ children, numeric }) {
  return (
    <th style={{
      background: 'var(--surface-2)',
      color: 'var(--muted)',
      fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em',
      fontSize: 9.5, padding: '12px 16px',
      textAlign: numeric ? 'right' : 'left',
      borderBottom: '1px solid var(--border)',
    }}>{children}</th>
  );
}

function Td({ children, numeric, className }) {
  return (
    <td className={className} style={{
      padding: '12px 16px', verticalAlign: 'middle',
      textAlign: numeric ? 'right' : 'left',
      ...(numeric ? { fontVariantNumeric: 'tabular-nums', fontFamily: '"JetBrains Mono", monospace' } : {}),
    }}>{children}</td>
  );
}

// ── Exportador CSV ────────────────────────────────────────────────
function exportarCSV(dados) {
  if (!dados?.length) return;
  const headers = [
    'codigo', 'tipo', 'empresa', 'cliente', 'operador',
    'dt_inicio', 'dt_vencimento', 'dias_corridos', 'dias_uteis',
    'princ_usd', 'spot', 'taxa_contratada',
    'cdi', 'sofr', 'spread_bps', 'despesa',
    'princ_brl', 'premio_bruto', 'ir', 'premio_liquido',
    'total_credito', 'taxa_final',
    'status', 'observacao',
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[,"\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = dados.map(d => [
    d.codigo_unico, d.tipo_operacao || 'ndf', d.empresa_nome, d.cliente_final, d.operador,
    d.dt_inicio, d.dt_vencimento, d.dc, d.du,
    d.princ_usd, d.spot, d.spot_venda,
    d.cdi, d.sofr, d.spread_bps, d.despesa,
    d.princ_brl?.toFixed(2), d.premio_bruto?.toFixed(2), d.ir_val?.toFixed(2), d.premio_liquido?.toFixed(2),
    d.total_credito?.toFixed(2), d.taxa_final?.toFixed(4),
    d.status, d.observacao,
  ].map(esc).join(','));
  const csv = headers.join(',') + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `cambio-fechamentos-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function IconBtn({ children, onClick, title, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 28, height: 28, borderRadius: 6,
        border: '1px solid transparent', background: 'transparent',
        color: danger ? 'var(--danger, #ef4444)' : 'var(--muted)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseOver={e => {
        e.currentTarget.style.background = 'var(--surface-3)';
        e.currentTarget.style.color = danger ? 'var(--danger, #ef4444)' : 'var(--ink)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
      onMouseOut={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = danger ? 'var(--danger, #ef4444)' : 'var(--muted)';
        e.currentTarget.style.borderColor = 'transparent';
      }}
    >{children}</button>
  );
}
